import React from 'react';

import { connectorLoginRedirect } from '@lumapps/connectors/utils/connectorLoginRedirect';
import { get } from '@lumapps/constants';
import { sendWebviewMobileMessage } from '@lumapps/mobile-webview/message-emit';

import { useRunMicroApp } from '../../hooks/useRunMicroApp';
import { useSubmitRunMicroApp } from '../../hooks/useSubmitRunMicroApp';
import { MicroAppProps, OpenUrlAction, SubmitMicroAppAction } from '../../types';
import { MicroAppCard } from '../MicroAppCard';

const { isMobileAppWebView } = get();

/**
 * Sets <MicroAppCard /> to display the given micro app.
 * The micro-app is fetched using the provided micro app id
 *
 * @param MicroAppProps
 * @returns MicroApp
 */
export const MicroApp: React.FC<MicroAppProps> = ({
    className,
    id: microAppId,
    setIsLoaded,
    runInstanceId,
    inputVariables,
}) => {
    const {
        data: microApp,
        isFetching,
        isError: fetchingHasFailed,
        error: fetchingError,
        refetch,
    } = useRunMicroApp(microAppId, runInstanceId, inputVariables, { enabled: Boolean(microAppId) });

    const { mutate: submitRunMicroApp, isLoading: isSubmitting, isError: submitHasFailed } = useSubmitRunMicroApp();

    const handleSubmit = (action: SubmitMicroAppAction) => {
        if (!microApp || !microApp.runId) {
            return;
        }
        submitRunMicroApp({
            action,
            microAppId,
            runId: microApp.runId,
            runInstanceId,
            inputVariables,
        });
    };

    const handleOpenUrl = async ({ url, connectorId, openUrlInNewTab }: OpenUrlAction) => {
        if (connectorId) {
            await connectorLoginRedirect({ connectorId, redirectUrl: url });
            refetch();
        } else if (isMobileAppWebView) {
            sendWebviewMobileMessage({ type: 'redirect', payload: { url, type: 'unknown' } });
        } else {
            window.open(url, openUrlInNewTab ? '_blank' : undefined);
        }
    };

    const showLoadingState = isFetching || isSubmitting;

    const showNotAuthorizedState = fetchingHasFailed && fetchingError?.response?.status === 403;

    const showErrorState = fetchingHasFailed || submitHasFailed;

    return (
        <MicroAppCard
            adaptiveCard={microApp?.adaptiveCard}
            className={className}
            hasError={showErrorState}
            id={microAppId}
            isLoading={showLoadingState}
            isNotAuthorized={showNotAuthorizedState}
            lumappsComponent={microApp?.lumappsComponent}
            onSubmit={handleSubmit}
            onOpenUrl={handleOpenUrl}
            setIsLoaded={setIsLoaded}
        />
    );
};

MicroApp.displayName = 'MicroApp';
